// FILE: settingsNavigation.ts
// Purpose: Share the settings topic taxonomy between the main sidebar and the settings screen.
// Layer: Route/UI support
// Exports: section ids, nav items, and search normalization helper

import type { GlobalPermission } from "@taskin-code/contracts";

import {
  AdjustmentsIcon,
  ArchiveIcon,
  BellIcon,
  BookIcon,
  BotIcon,
  BrainIcon,
  ChartIcon,
  FolderIcon,
  ListChecksIcon,
  LockIcon,
  type LucideIcon,
  PaletteIcon,
  SettingsIcon,
  TagsIcon,
  TeamsIcon,
  UserIcon,
  UsersIcon,
  WrenchIcon,
  WorktreeIcon,
} from "./lib/icons";

export const SETTINGS_SECTION_IDS = [
  "general",
  "appearance",
  "notifications",
  "behavior",
  "worktrees",
  "archived",
  "profile",
  "security",
  "users",
  "teams",
  "agents",
  "memories",
  "tags",
  "projects",
  "plane",
  "models",
  "usage",
  "advanced",
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];

/**
 * Section ids that no longer exist, and where they went.
 *
 * Repositorios, Entornos, Proyectos vinculados and Comandos locales were four
 * screens that each opened by asking "which project?"; they are now tabs of
 * Proyectos, which asks once. The ids stay reachable because they are in
 * bookmarks and in links people have pasted to each other — resolving them to
 * General would look like the feature was removed.
 */
const SETTINGS_SECTION_ALIASES: Readonly<Record<string, SettingsSectionId>> = {
  repositories: "projects",
  environments: "projects",
  "linked-projects": "projects",
  "local-commands": "projects",
};

export type SettingsNavGroupId = "app" | "taskin-code";

/**
 * How much horizontal canvas a section's panel gets.
 *
 * `narrow` is the default and the right answer for preference forms, where a
 * short measure is what makes them readable. `wide` fits the admin tables.
 * `full` is for panels whose content is genuinely tabular and wide — capping
 * those only moves the columns behind a horizontal scrollbar.
 */
export type SettingsSectionWidth = "narrow" | "wide" | "full";

export type SettingsNavItem = {
  id: SettingsSectionId;
  group: SettingsNavGroupId;
  label: string;
  description: string;
  icon: LucideIcon;
  eyebrow: string;
  /** Defaults to `narrow` when omitted. */
  width?: SettingsSectionWidth;
  /**
   * Global permission required to see this section at all — in the sidebar menu
   * and by URL alike. Omitted means everyone signed in may open it, which is
   * the right answer for per-browser preferences and for a user's own data.
   *
   * Declared here rather than in the sidebar so the menu and the settings route
   * cannot drift: both read this field through `isSettingsSectionVisible`, and
   * adding a section without deciding its permission is a choice the type makes
   * you look at.
   *
   * Where a section already has a permission the server enforces, that is the
   * one named here rather than a parallel `settings.*` key — Repositories and
   * Plane reuse the credential permissions their endpoints already demand, so
   * the menu can never offer a screen the backend will refuse.
   */
  requiredPermission?: GlobalPermission;
};

export const SETTINGS_NAV_GROUPS: ReadonlyArray<{
  id: SettingsNavGroupId;
  label: string;
}> = [
  { id: "app", label: "App" },
  { id: "taskin-code", label: "Taskin Code" },
] as const;

export const SETTINGS_NAV_ITEMS: readonly SettingsNavItem[] = [
  {
    id: "general",
    group: "app",
    label: "General",
    description: "Default provider, thread mode, and sidebar organization.",
    icon: SettingsIcon,
    eyebrow: "Workflow defaults",
  },
  {
    id: "appearance",
    group: "app",
    label: "Appearance",
    description: "Theme, typography, and timestamp formatting.",
    icon: PaletteIcon,
    eyebrow: "Visual language",
  },
  {
    id: "notifications",
    group: "app",
    label: "Notifications",
    description: "In-app toasts and desktop alerts.",
    icon: BellIcon,
    eyebrow: "Alerts",
  },
  {
    id: "behavior",
    group: "app",
    label: "Behavior",
    description: "Streaming, diff handling, and destructive confirmations.",
    icon: AdjustmentsIcon,
    eyebrow: "Interaction rules",
  },
  {
    id: "worktrees",
    group: "app",
    label: "Worktrees",
    description: "Review and clean up the worktrees created by Taskin Code.",
    icon: WorktreeIcon,
    eyebrow: "Workspace management",
  },
  {
    id: "archived",
    group: "app",
    label: "Archived",
    description: "View and restore archived threads.",
    icon: ArchiveIcon,
    eyebrow: "Thread management",
  },
  {
    id: "profile",
    group: "taskin-code",
    label: "Profile",
    description: "Your name and the photo shown next to it.",
    icon: UserIcon,
    eyebrow: "Your account",
  },
  {
    id: "security",
    group: "taskin-code",
    label: "Security",
    description: "Two-factor authentication for your own account.",
    icon: LockIcon,
    eyebrow: "Account protection",
  },
  {
    id: "users",
    group: "taskin-code",
    label: "Users",
    description: "Manage user accounts, permissions, and project assignments.",
    icon: UsersIcon,
    eyebrow: "Access control",
    width: "wide",
    requiredPermission: "u.view",
  },
  {
    id: "teams",
    group: "taskin-code",
    label: "Teams",
    description: "Group users so they can see and share threads with each other.",
    icon: TeamsIcon,
    eyebrow: "Access control",
    width: "wide",
    // Teams is administered from the same screen family as Users and gated on
    // the same permission server-side (`teamAdminRoutes.ts` lists on `u.view`).
    requiredPermission: "u.view",
  },
  {
    id: "agents",
    group: "taskin-code",
    label: "Agents",
    description: "Define custom agents with their own model, instructions, tools, and subagents.",
    icon: BotIcon,
    eyebrow: "AI configuration",
    width: "wide",
    requiredPermission: "a.view",
  },
  {
    id: "memories",
    group: "taskin-code",
    label: "Memoria",
    description: "Lo que los agentes ya saben de esta instalación, y lo que proponen aprender.",
    icon: BookIcon,
    eyebrow: "AI configuration",
    width: "wide",
    // Deliberately ungated, like `local-commands`: the panel serves two
    // audiences. Anyone may write their own `user` memories — those are their
    // standing preferences, and gating them would mean asking an administrator
    // to state your own — while the general, project and proposal tabs need
    // `m.view`/`m.edit` and the panel hides them when the viewer lacks it.
    // `memoryAdminRoutes.ts` enforces the same split server-side.
  },
  {
    id: "tags",
    group: "taskin-code",
    label: "Etiquetas",
    description: "El vocabulario que decide a qué agentes y proyectos les sirve cada cosa.",
    icon: TagsIcon,
    eyebrow: "AI configuration",
    requiredPermission: "t.view",
  },
  {
    id: "projects",
    group: "taskin-code",
    label: "Proyectos",
    description: "Repositorio, tablero, vínculos, comandos locales, entornos y etiquetas.",
    icon: FolderIcon,
    eyebrow: "Administración de proyectos",
    width: "wide",
    // Deliberately ungated, and the reason is structural: everything this
    // screen edits is gated on `p.edit` *on a project*, and this field can only
    // name a global permission. Someone who administers one project of ten
    // holds no global grant at all, so any value here would hide the screen
    // from exactly the person it exists for. The panel asks per project, and
    // shows only the tabs that project's grants allow.
  },
  {
    id: "plane",
    group: "taskin-code",
    label: "Plane",
    description: "La instancia de Plane de la instalación y tu credencial en ella.",
    icon: ListChecksIcon,
    eyebrow: "Gestión de tareas",
    width: "wide",
    // Only the installation-wide halves live here now: which Plane the whole
    // organisation talks to, and each person's own token for it. Which board a
    // project follows moved to Proyectos → Plane.
    requiredPermission: "p.project.set_plane_credentials",
  },
  {
    id: "models",
    group: "taskin-code",
    label: "Models",
    description: "Git writing defaults and custom model slugs.",
    icon: BrainIcon,
    eyebrow: "AI configuration",
    requiredPermission: "settings.models.view",
  },
  {
    id: "usage",
    group: "taskin-code",
    label: "Usage",
    description: "Token consumption and cost, grouped by model, user, thread, agent, or day.",
    icon: ChartIcon,
    eyebrow: "Cost & accounting",
    // Seven numeric columns plus a filter row: anything narrower than the
    // viewport just hides Cost and Turns behind a scrollbar.
    width: "full",
    requiredPermission: "usage.view",
  },
  {
    id: "advanced",
    group: "taskin-code",
    label: "Advanced",
    description: "Provider installs, keybindings, recovery, and version info.",
    icon: WrenchIcon,
    eyebrow: "System tools",
    requiredPermission: "settings.advanced.view",
  },
] as const;

/**
 * The section a user lands on when the one they asked for is not theirs to see.
 *
 * General carries no permission by design, so this is always reachable.
 */
export const SETTINGS_FALLBACK_SECTION: SettingsSectionId = "general";

export function normalizeSettingsSection(value: unknown): SettingsSectionId {
  if (typeof value !== "string") {
    return SETTINGS_FALLBACK_SECTION;
  }
  const known = SETTINGS_SECTION_IDS.find((candidate) => candidate === value);
  if (known !== undefined) return known;
  return SETTINGS_SECTION_ALIASES[value] ?? SETTINGS_FALLBACK_SECTION;
}

/**
 * Whether `section` may be shown to a user holding `globalPermissions`.
 *
 * The single decision the sidebar menu and the settings route both defer to, so
 * an item that is hidden from the menu cannot be reached by typing its URL and
 * an item that is reachable cannot be missing from the menu.
 *
 * This governs *visibility only*. The server remains the security boundary:
 * every section whose data lives server-side is gated again in its own route
 * handler, and the section components keep their own "you cannot see this"
 * guards as a second layer.
 */
export function isSettingsSectionVisible(
  section: SettingsSectionId,
  globalPermissions: ReadonlySet<GlobalPermission>,
): boolean {
  const item = SETTINGS_NAV_ITEMS.find((candidate) => candidate.id === section);
  if (!item) return false;
  return item.requiredPermission === undefined || globalPermissions.has(item.requiredPermission);
}

/** The nav items a user holding `globalPermissions` may see, in catalogue order. */
export function visibleSettingsNavItems(
  globalPermissions: ReadonlySet<GlobalPermission>,
): readonly SettingsNavItem[] {
  return SETTINGS_NAV_ITEMS.filter(
    (item) =>
      item.requiredPermission === undefined || globalPermissions.has(item.requiredPermission),
  );
}

/**
 * `normalizeSettingsSection` plus the permission check: an unknown *or*
 * forbidden section resolves to General rather than rendering a panel the user
 * was never offered.
 */
export function resolveSettingsSection(
  value: unknown,
  globalPermissions: ReadonlySet<GlobalPermission>,
): SettingsSectionId {
  const section = normalizeSettingsSection(value);
  return isSettingsSectionVisible(section, globalPermissions) ? section : SETTINGS_FALLBACK_SECTION;
}
